import React, { type MouseEvent, useRef, useState } from 'react';
import { ApplicationView } from '@aha-app/mvc';
import { useEventHandler } from '@aha-app/aha-editor-v2';
import styles from './ResizerHandle.module.css';
import classNames from 'classnames';

// Resize the artifact panel.
const ResizerHandle = ({
  onResize,
  onStartResize,
  onEndResize,
}: {
  onResize: (number) => void;
  onStartResize: () => void;
  onEndResize: () => void;
}) => {
  const handleRef = useRef<HTMLDivElement>(null);
  const [dragging, setDragging] = useState(false);

  const width = (x: number) => {
    if (!handleRef.current?.parentElement?.parentElement) return 'inherit';

    const parentRect =
      handleRef.current.parentElement.parentElement.getBoundingClientRect();

    const percentage = 100 - (100 * x) / parentRect.right;

    // Adjust for the margin on the right of the artifact panel and the offset of the .resizer.
    return `calc(${Math.min(
      Math.max(percentage, 20),
      80
    )}vw - var(--spacer-2) - 3px)`;
  };

  const onMouseDown = (event: MouseEvent) => {
    setDragging(true);

    if (onStartResize) onStartResize();
    if (onResize) onResize(width(event.clientX));

    event.stopPropagation();
    event.preventDefault();
  };

  const onMouseMove = (event: MouseEvent) => {
    if (dragging) {
      event.stopPropagation();
      event.preventDefault();

      if (onResize) onResize(width(event.pageX));

      event.stopPropagation();
      event.preventDefault();
    }
  };

  const onMouseUp = (event: MouseEvent) => {
    if (dragging) {
      setDragging(false);
      if (onEndResize) onEndResize();
      if (onResize) onResize(width(event.clientX));

      event.stopPropagation();
      event.preventDefault();
    }
  };

  useEventHandler('mousemove', (event: MouseEvent) => onMouseMove(event));
  useEventHandler('mouseup', (event: MouseEvent) => onMouseUp(event));

  return (
    <div
      className={styles.resizer}
      onMouseDown={onMouseDown}
      onMouseUp={onMouseUp}
    >
      <div
        ref={handleRef}
        className={classNames({
          [styles.handle]: true,
          [styles.dragging]: dragging,
        })}
      />
    </div>
  );
};

export default ApplicationView(ResizerHandle);
